import { useCallback, useEffect, useRef } from "react";

/** Runs an action only while the component that asked for it is still shown. */
export type WhileShown = (action: () => void) => void;

/**
 * Returns a guard for what follows a mutation beyond the component itself (F-63): closing its form
 * or panel in the parent, navigating, starting a download or another mutation. With
 * {@link mutateThen} a follow-up also runs after the component unmounted – the person cancelled,
 * opened another entry or left the page, or a mutation paused offline settled much later – and such
 * an action would then close another form, jump to another page or save a file out of context.
 * Inside the guard it is skipped once the component is gone; an announcement next to it still runs.
 *
 * @returns The guard; stable for the life of the component.
 */
export function useWhileShown(): WhileShown {
  const shown = useRef(false);

  useEffect(() => {
    shown.current = true;

    return (): void => {
      shown.current = false;
    };
  }, []);

  const whileShown = useCallback((action: () => void): void => {
    if (shown.current) {
      action();
    }
  }, []);

  return whileShown;
}
